import { Dialog, dialogContext, MessageDialog, useDialogManager } from '../dialog';
import { removeNotification, showNotification } from '../notificationToaster';
import React, { FormEvent, FunctionComponent, Suspense, useContext, useEffect, useState } from 'react';
import { graphql } from 'react-relay';
import { useLazyLoadQuery, useMutation } from 'react-relay/hooks';
import { useNavigate } from 'react-router-dom';
import config from 'src/shared/config';
import { stringFormat, T } from 'src/shared/helpers';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import styled from 'styled-components';
import { activateAddonDialogMutation } from './__generated__/activateAddonDialogMutation.graphql';
import { activateAddonDialogQuery, activateAddonDialogQuery$data } from './__generated__/activateAddonDialogQuery.graphql';

const ContentDialogUpgrade = styled.p`
    font-size: 14px;
    line-height: 20px;
    margin-bottom: 20px;
`;

const ActivateAddonDialogQuery: FunctionComponent<{
    addonKey: string;
    currencyCode: string;
    onLoaded: (data: activateAddonDialogQuery$data) => void;
}> = ({ addonKey, currencyCode, onLoaded }) => {
    const query = useLazyLoadQuery<activateAddonDialogQuery>(
        graphql`
            query activateAddonDialogQuery($addonKey: String!, $currencyCode: String!) {
                addon(addonKey: $addonKey, currencyCode: $currencyCode) {
                    addonID
                    billingSystemAddonID
                    name
                    price
                    description
                    rates {
                        feeValue
                        feePerc
                    }
                }
                ecommerceSettings {
                    ecommerceName
                }
            }
        `,
        { addonKey, currencyCode }
    );

    useEffect(() => {
        onLoaded(query);
    }, []);

    return null;
};

export const ActivateAddonDialog: FunctionComponent<{
    message: React.ReactNode;
    addonKey: string;
    addonName: string;
    showCloseButton?: boolean;
    redirectUrlFromDashboard?: string;
    redirectUrlAddonNoActivableForDevStore?: string;
    onCloseButtonClick?: () => void;
    onActivateCompleted?: () => void;
    onUpdater?: (store: import('relay-runtime').RecordSourceSelectorProxy) => void;
}> = ({
    message,
    addonKey,
    addonName,
    showCloseButton,
    redirectUrlFromDashboard,
    redirectUrlAddonNoActivableForDevStore,
    onCloseButtonClick,
    onActivateCompleted,
    onUpdater
}) => {
    const { showDialog, closeDialog } = useDialogManager();
    const { shopifyEcommerceSettings } = useSubscriptionData();
    const { subscription } = useSubscriptionData();
    const { dialogId } = useContext(dialogContext);
    const [query, setQuery] = useState<activateAddonDialogQuery$data | null>(null);
    const navigate = useNavigate();

    // State
    const [isStartActivating, setIsStartActivating] = useState(false);
    const [isInternalStartActivating, setIsInternalStartActivating] = useState(false);

    // Mutations
    const [activateAddon] = useMutation<activateAddonDialogMutation>(graphql`
        mutation activateAddonDialogMutation($addonId: Long!) {
            activateAddon(addonId: $addonId)
        }
    `);

    // Callbacks
    const urlAction = 'Subscription/Seller/Addons/ActivateAddon';

    const onSubmitActivateAddonForm = (e: FormEvent) => {
        e.preventDefault();

        if (shopifyEcommerceSettings?.isTestStore === true && config.isProduction) {
            showDialog(
                'test-store',
                <MessageDialog
                    buttons={[
                        {
                            label: T._('OK', 'Admin'),
                            onClick: () => {
                                if (redirectUrlAddonNoActivableForDevStore) navigate(redirectUrlAddonNoActivableForDevStore);

                                closeDialog('test-store');
                                closeDialog(dialogId);
                            }
                        }
                    ]}
                    showCloseButton={false}
                    message={
                        <>
                            {stringFormat(
                                T._("Sorry! Your account is currently a development store, which means you can't add '{0}' add-on at the moment.", 'Admin'),
                                addonName
                            )}
                            <br />
                            {T._('To access this feature, all you need to do is upgrade to an active Shopify plan.', 'Admin')}
                            <br />
                            <br />
                            {T._('Thanks for your understanding!', 'Admin')}
                        </>
                    }
                />
            );
            return;
        }

        showNotification({
            type: 'default',
            message: T._('Activating addon...', 'Admin')
        });

        setIsStartActivating(true);
        const form = e.currentTarget as HTMLFormElement;
        form.submit();
    };

    const onActivateAddon = () => {
        if (shopifyEcommerceSettings?.isTestStore === true && config.isProduction) {
            showDialog(
                'test-store',
                <MessageDialog
                    buttons={[
                        {
                            label: T._('OK', 'Admin'),
                            onClick: () => {
                                if (redirectUrlAddonNoActivableForDevStore) navigate(redirectUrlAddonNoActivableForDevStore);

                                closeDialog('test-store');
                                closeDialog(dialogId);
                            }
                        }
                    ]}
                    showCloseButton={false}
                    message={
                        <>
                            {stringFormat(
                                T._("Sorry! Your account is currently a development store, which means you can't add '{0}' add-on at the moment.", 'Admin'),
                                addonName
                            )}
                            <br />
                            {T._('To access this feature, all you need to do is upgrade to an active Shopify plan.', 'Admin')}
                            <br />
                            <br />
                            {T._('Thanks for your understanding!', 'Admin')}
                        </>
                    }
                />
            );
            return;
        }

        setIsInternalStartActivating(true);
        const activateInProgressNotification = showNotification({
            type: 'default',
            message: T._('Activating addon...', 'Admin')
        });

        activateAddon({
            onCompleted: (response) => {
                setIsInternalStartActivating(false);
                removeNotification(activateInProgressNotification);
                closeDialog(dialogId);
                if (response.activateAddon != 'SUCCESS') {
                    showNotification({
                        type: 'danger',
                        message:
                            response.activateAddon === 'NO_PREMIUM'
                                ? T._('Unable to activate the addon cause you have to upgrade your plan', 'Admin')
                                : T._('An error occurred while activate addon', 'Admin'),
                        dismiss: {
                            duration: 5000
                        }
                    });
                } else {
                    showNotification({
                        type: 'success',
                        message: T._('Addon activated successfully', 'Admin'),
                        dismiss: {
                            duration: 5000
                        }
                    });
                    if (onActivateCompleted) onActivateCompleted();
                }
            },
            updater: (store) => {
                onUpdater?.(store);
            },
            variables: {
                addonId: query!.addon!.addonID
            }
        });
    };

    const ComponentToRender = (
        <Dialog
            size='small'
            showCloseButton={showCloseButton ?? true}
            onClose={onCloseButtonClick}
            buttons={[
                {
                    label: T._('Activate', 'Admin'),
                    disabled: isStartActivating || isInternalStartActivating,
                    isSubmit: query?.ecommerceSettings?.ecommerceName === 'SHOPIFY' && query.addon?.billingSystemAddonID !== null,
                    onClick: query?.ecommerceSettings?.ecommerceName !== 'SHOPIFY' || !query.addon?.billingSystemAddonID ? onActivateAddon : undefined
                }
            ]}
        >
            <ContentDialogUpgrade>
                {message}
                <br />
                {addonKey === 'LARGE_FILES' && (
                    <span>
                        {stringFormat(
                            T._(
                                "Seamlessly receive print-ready files for high quality large-format printing. Try it free for 14 days, then you'll pay a monthly fee of {0} {1} on top of your plan.",
                                'Admin'
                            ),
                            (query?.addon?.price ?? 0).toFixed(2).replace('.', ','),
                            subscription?.plan?.currencyCode ?? 'USD'
                        )}
                    </span>
                )}
            </ContentDialogUpgrade>
        </Dialog>
    );

    // Query for componenent
    if (!query) {
        return (
            <React.Fragment>
                {ComponentToRender}
                <Suspense fallback={null}>
                    <ActivateAddonDialogQuery
                        onLoaded={setQuery}
                        addonKey={addonKey}
                        currencyCode={subscription?.plan?.currencyCode ?? 'USD'}
                    ></ActivateAddonDialogQuery>
                </Suspense>
            </React.Fragment>
        );
    }

    return (
        <form method='POST' onSubmit={onSubmitActivateAddonForm} action={urlAction} data-t1={'false'}>
            <input name='__RequestVerificationToken' type='hidden' value={config.antiforgeryToken}></input>
            <input type='hidden' name='addonID' value={query.addon?.addonID} />
            <input type='hidden' name='redirectUrlFromDashboard' value={redirectUrlFromDashboard ?? ''} />
            {ComponentToRender}
        </form>
    );
};
